import axios from 'axios';
import { BASE_URL } from '../api/config';

// Service du bloc « Suivi client (site Inde tourisme) » de la fiche d'un dossier (lot 5 de l'espace client Inde
// tourisme) : lecture seule de GET /suivi-client/demandes/:id (backend admin, module suiviClient/ du lot 4).
// La route répond { applicable: false } pour un dossier qui n'est pas du site tourisme (demandes.source).
// recupererSuiviDossier renvoie null quand le suivi est indisponible (404 : route absente tant que le backend n'est
// pas redémarré ; 503 : tables absentes ; réseau) : le bloc disparaît alors sans bruit. Les autres erreurs remontent.
// La réponse est normalisée (champ absent ou mal typé remplacé par une valeur neutre) : un écart du backend ne doit
// jamais faire planter l'écran des demandes au rendu.

// demandes.source des dossiers du site Inde tourisme : seuls concernés par l'espace client et les mails de suivi
export const SOURCE_INDE_TOURISME = 'externe_inde_tourisme';

export type TonPastille = 'attente' | 'ok' | 'warn' | 'ko' | 'neutre';
export type EtatJalon = 'fait' | 'actif' | 'probleme' | 'a_venir' | 'arrete';
export type StatutJournal =
  'envoye' | 'indetermine' | 'dry_run' | 'echec' | 'abandon' | 'modele_inactif' | 'amorcage' | 'retour_arriere';

export interface PastilleSuivi {
  texte: string;
  ton: TonPastille;
}

export interface JalonSuivi {
  code: string;  // formulaire, documents, verification, depot, evisa, earrival
  titre: string; // « Dossier vérifié »
  court: string; // « Vérifié »
  etat: EtatJalon;
  meta: string;  // « 20/10 », « Sous 3 jours ouvrés »…
}

// Étape telle que le client la voit dans son espace (sortie de calculerSuivi, suiviClient/etapes.js)
export interface EtapeSuivi {
  niveau: number | null; // 2 à 6
  statut: string;        // en_cours | refuse | annule | rembourse
  pastille: PastilleSuivi;
  phrase: string;
  jalons: JalonSuivi[];
  // false : dossier absent de l'espace client (archivé sans eVisa reçu) ; seulement si le backend l'envoie
  visible?: boolean;
}

// Mémoire de la veille (table suivi_client) ; null tant que la veille n'a pas vu le dossier
export interface MemoireSuivi {
  niveau_annonce: number | null;
  niveau_vu: number | null;
  vu_depuis: string | null;
  dates_etapes: Record<string, string>; // { verification: 'AAAA-MM-JJ HH:MM:SS', … }
}

// Ligne de suivi_client_journal
export interface EntreeJournalSuivi {
  date_creation: string;
  code_mail: string;    // vide pour un événement sans mail (amorçage, retour en arrière)
  libelle_mail: string;
  niveau: number | null;
  statut: string;       // StatutJournal attendu ; une valeur inconnue est affichée telle quelle
  sujet: string;
  detail: string;
}

export interface SuiviApplicable {
  applicable: true;
  dry_run: boolean;
  email: string;                     // adresse réelle à laquelle partent les mails de suivi
  etape: EtapeSuivi;
  suivi: MemoireSuivi | null;
  journal: EntreeJournalSuivi[];     // 50 derniers, plus récent d'abord
  derniere_connexion: string | null; // 'AAAA-MM-JJ HH:MM:SS', null si jamais connecté
}

export type SuiviDossier = { applicable: false } | SuiviApplicable;

export type CouleurStatut = 'default' | 'success' | 'info' | 'warning' | 'error';

export interface InfoStatut {
  libelle: string;
  couleur: CouleurStatut;
  aide: string; // explication au survol de la puce
}

// Puces du journal
export const STATUTS_JOURNAL: Record<StatutJournal, InfoStatut> = {
  envoye: { libelle: 'envoyé', couleur: 'success', aide: 'Mail accepté par Brevo' },
  dry_run: { libelle: 'simulation', couleur: 'info', aide: 'Mode simulation : mail préparé, rien n\'est parti' },
  echec: { libelle: 'échec', couleur: 'error', aide: 'Mail refusé par Brevo' },
  abandon: { libelle: 'abandon', couleur: 'error', aide: 'Envoi abandonné après plusieurs refus de Brevo' },
  modele_inactif: { libelle: 'modèle désactivé', couleur: 'warning', aide: 'Modèle désactivé dans le menu Mails : rien n\'est parti' },
  amorcage: { libelle: 'amorçage', couleur: 'default', aide: 'Mise en service : étape déjà atteinte notée comme annoncée, sans mail' },
  retour_arriere: { libelle: 'retour en arrière', couleur: 'default', aide: 'L\'état du dossier a reculé : aucun mail' },
  indetermine: { libelle: 'non confirmé', couleur: 'warning', aide: 'Envoi sans confirmation de Brevo : le mail a pu partir' },
};

// Libellé d'un événement sans mail (code_mail vide)
const LIBELLES_SANS_MAIL: Record<string, string> = {
  amorcage: 'Mise en service du suivi',
  retour_arriere: 'Retour en arrière de l\'état',
};

const TONS: TonPastille[] = ['attente', 'ok', 'warn', 'ko', 'neutre'];
const ETATS: EtatJalon[] = ['fait', 'actif', 'probleme', 'a_venir', 'arrete'];

// Lecture d'une clé propre à l'objet (jamais toString, constructor… hérités du prototype)
const propre = <T>(o: Record<string, T>, cle: string): T | undefined =>
  (Object.prototype.hasOwnProperty.call(o, cle) ? o[cle] : undefined);

export const statutJournal = (statut: string): InfoStatut =>
  propre<InfoStatut>(STATUTS_JOURNAL, statut) || { libelle: statut || 'inconnu', couleur: 'default', aide: '' };

// Libellé affiché pour une ligne du journal : libellé du modèle, sinon son code, sinon l'événement (et son étape)
export const libelleEntree = (e: EntreeJournalSuivi): string => {
  if (e.libelle_mail) return e.libelle_mail;
  if (e.code_mail) return e.code_mail;
  const base = propre(LIBELLES_SANS_MAIL, e.statut) || 'Événement du suivi';
  return e.niveau ? `${base} (étape ${e.niveau})` : base;
};

const pad2 = (n: number): string => (n < 10 ? '0' : '') + n;

// 'AAAA-MM-JJ HH:MM:SS' (heure du serveur, affichée telle quelle), 'AAAA-MM-JJ', ou ISO 8601 avec fuseau (date
// sérialisée par le driver MySQL, convertie en heure locale) -> 'JJ/MM/AAAA HH:MM' ('JJ/MM/AAAA' sans heure) ;
// la chaîne telle quelle si elle est illisible, '' si vide
export const formaterDateSuivi = (v: string | null | undefined): string => {
  const s = (v || '').trim();
  if (!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?$/.exec(s);
  if (m) return m[4] ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// ---------- Normalisation de la réponse ----------

const estObjet = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const texte = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
};

const nombre = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN);
  return Number.isFinite(n) ? n : null;
};

const vrai = (v: unknown): boolean => v === true || v === 1 || v === 'true';

const normaliserJalon = (j: unknown): JalonSuivi | null => {
  if (!estObjet(j)) return null;
  const etat = ETATS.indexOf(j.etat as EtatJalon) >= 0 ? (j.etat as EtatJalon) : 'a_venir';
  return { code: texte(j.code), titre: texte(j.titre), court: texte(j.court) || texte(j.titre), etat, meta: texte(j.meta) };
};

const normaliserEtape = (brut: unknown): EtapeSuivi => {
  const e: Record<string, unknown> = estObjet(brut) ? brut : {};
  const p: Record<string, unknown> = estObjet(e.pastille) ? e.pastille : {};
  const etape: EtapeSuivi = {
    niveau: nombre(e.niveau),
    statut: texte(e.statut),
    pastille: {
      texte: texte(p.texte) || 'Étape inconnue',
      ton: TONS.indexOf(p.ton as TonPastille) >= 0 ? (p.ton as TonPastille) : 'neutre',
    },
    phrase: texte(e.phrase),
    jalons: (Array.isArray(e.jalons) ? e.jalons : [])
      .map(normaliserJalon)
      .filter((j): j is JalonSuivi => j !== null),
  };
  if (typeof e.visible === 'boolean') etape.visible = e.visible;
  return etape;
};

const normaliserMemoire = (brut: unknown): MemoireSuivi | null => {
  if (!estObjet(brut)) return null;
  let dates: unknown = brut.dates_etapes;
  if (typeof dates === 'string') {
    // Colonne JSON renvoyée en chaîne par certains drivers
    try { dates = JSON.parse(dates); } catch { dates = null; }
  }
  const datesEtapes: Record<string, string> = {};
  if (estObjet(dates)) {
    const source = dates;
    Object.keys(source).forEach((cle) => {
      const v = texte(source[cle]);
      if (v) datesEtapes[cle] = v;
    });
  }
  return {
    niveau_annonce: nombre(brut.niveau_annonce),
    niveau_vu: nombre(brut.niveau_vu),
    vu_depuis: texte(brut.vu_depuis) || null,
    dates_etapes: datesEtapes,
  };
};

const normaliserEntree = (brut: unknown): EntreeJournalSuivi | null => {
  if (!estObjet(brut)) return null;
  return {
    date_creation: texte(brut.date_creation),
    code_mail: texte(brut.code_mail),
    libelle_mail: texte(brut.libelle_mail),
    niveau: nombre(brut.niveau),
    statut: texte(brut.statut),
    sujet: texte(brut.sujet),
    detail: texte(brut.detail),
  };
};

// null si la réponse n'est pas un objet (page HTML d'un proxy, corps vide…) : traité comme un suivi indisponible
export const normaliserSuivi = (brut: unknown): SuiviDossier | null => {
  if (!estObjet(brut)) return null;
  if (brut.applicable !== true) return { applicable: false };
  return {
    applicable: true,
    dry_run: vrai(brut.dry_run),
    email: texte(brut.email),
    etape: normaliserEtape(brut.etape),
    suivi: normaliserMemoire(brut.suivi),
    journal: (Array.isArray(brut.journal) ? brut.journal : [])
      .map(normaliserEntree)
      .filter((e): e is EntreeJournalSuivi => e !== null),
    derniere_connexion: texte(brut.derniere_connexion) || null,
  };
};

// ---------- Appel ----------

// Suivi indisponible : aucune réponse (réseau, CORS, serveur injoignable), 404 (route absente tant que le backend
// admin n'est pas redémarré), 503 (tables du suivi absentes)
export const suiviIndisponible = (e: unknown): boolean => {
  if (!axios.isAxiosError(e)) return false;
  if (!e.response) return true;
  return e.response.status === 404 || e.response.status === 503;
};

// Les mails automatiques de suivi partent-ils vraiment ? Vrai seulement si la veille répond, avec ses tables et hors
// simulation (GET /suivi-client/etat). Sinon (simulation, backend pas encore à jour, réseau) : false, et les dialogues
// « Uploader Visa » / « Upload E-card » gardent le mail manuel coché, comme avant : le client est toujours prévenu.
export const mailsSuiviActifs = async (): Promise<boolean> => {
  try {
    const r = await axios.get(`${BASE_URL}/suivi-client/etat`);
    const d = (r && r.data) || {};
    return d.tables_ok === true && (d.dry_run === false || d.dry_run === 'false' || d.dry_run === 0);
  } catch (e) {
    return false;
  }
};

export const recupererSuiviDossier = async (idDemande: number): Promise<SuiviDossier | null> => {
  try {
    const r = await axios.get(`${BASE_URL}/suivi-client/demandes/${encodeURIComponent(String(idDemande))}`);
    return normaliserSuivi(r.data);
  } catch (e) {
    if (suiviIndisponible(e)) return null;
    throw e;
  }
};
